import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, useState } from "react";
import { opsFetch } from "../lib/api";
import { Count, Pill, Thumb } from "../components/ui";
import { CityPreview } from "../components/PreviewPane";
import { SplitPane } from "../components/SplitPane";
import { downloadCsv } from "../lib/exportCsv";

type Dest = {
  id: string;
  slug: string;
  status: string;
  heat: string;
  name_en: string;
  name_zh: string;
  blurb_en: string;
  blurb_zh: string;
  image_src: string;
};

type ListRes = { data: { items: Dest[]; total: number } };

const EMPTY = {
  slug: "",
  status: "draft",
  heat: "P1",
  name_en: "",
  name_zh: "",
  blurb_en: "",
  blurb_zh: "",
  image_src: "",
};

export function DestinationsPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const [heat, setHeat] = useState("");
  const [qtext, setQtext] = useState("");
  const [editing, setEditing] = useState<typeof EMPTY | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["ops-destinations", status],
    queryFn: () => {
      const params = new URLSearchParams({ per_page: "100" });
      if (status) params.set("status", status);
      return opsFetch<ListRes>(`/api/ops/destinations?${params}`);
    },
  });

  const save = useMutation({
    mutationFn: (body: typeof EMPTY) =>
      opsFetch(`/api/ops/destinations/${encodeURIComponent(body.slug.trim().toLowerCase())}`, {
        method: "PUT",
        body: JSON.stringify({ ...body, slug: body.slug.trim().toLowerCase() }),
      }),
    onSuccess: () => {
      setMsg("Saved");
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ["ops-destinations"] });
    },
    onError: (e: Error) => setMsg(e.message),
  });

  const archive = useMutation({
    mutationFn: (slug: string) =>
      opsFetch(`/api/ops/destinations/${encodeURIComponent(slug)}`, { method: "DELETE" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["ops-destinations"] }),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!editing?.slug || !editing.name_en) return;
    save.mutate(editing);
  }

  const needle = qtext.trim().toLowerCase();
  const shown = (q.data?.data.items ?? []).filter((item) => {
    if (heat && item.heat !== heat) return false;
    if (!needle) return true;
    return `${item.name_en} ${item.name_zh} ${item.slug}`.toLowerCase().includes(needle);
  });
  const row = shown.find((item) => item.id === picked) ?? shown[0];
  const preview = editing
    ? {
        name: editing.name_en,
        nameZh: editing.name_zh,
        blurb: editing.blurb_en,
        blurbZh: editing.blurb_zh,
        image: editing.image_src,
        heat: editing.heat,
        status: editing.status,
      }
    : {
        name: row?.name_en || "",
        nameZh: row?.name_zh,
        blurb: row?.blurb_en,
        blurbZh: row?.blurb_zh,
        image: row?.image_src,
        heat: row?.heat,
        status: row?.status,
      };

  return (
    <SplitPane>
      <div>
      <div className="ops-toolbar">
        <button className="ops-btn ops-btn--primary" onClick={() => setEditing({ ...EMPTY })} type="button">
          New destination
        </button>
        <select className="ops-input" onChange={(e) => setStatus(e.target.value)} value={status}>
          <option value="">All</option>
          <option value="draft">draft</option>
          <option value="publish">publish</option>
          <option value="archived">archived</option>
        </select>
        <select className="ops-input" onChange={(e) => setHeat(e.target.value)} value={heat}>
          <option value="">All heat</option>
          <option value="P0">P0</option>
          <option value="P1">P1</option>
          <option value="P2">P2</option>
        </select>
        <input className="ops-input" onChange={(e) => setQtext(e.target.value)} placeholder="Search city" value={qtext} />
        <button
          className="ops-btn"
          onClick={() =>
            downloadCsv(
              "destinations.csv",
              ["slug", "name", "name_zh", "heat", "status"],
              shown.map((item) => [item.slug, item.name_en, item.name_zh, item.heat, item.status]),
            )
          }
          type="button"
        >
          Export
        </button>
        {q.data ? <Count label="cities" n={shown.length} /> : null}
        {msg ? <span className="ops-ok">{msg}</span> : null}
      </div>

      {editing ? (
        <form className="ops-panel ops-form" onSubmit={onSubmit}>
          <div className="ops-form__grid">
            {(
              [
                ["slug", "Slug"],
                ["name_en", "Name EN"],
                ["name_zh", "Name ZH"],
                ["heat", "Heat (P0/P1/P2)"],
                ["image_src", "Image URL"],
                ["status", "Status"],
              ] as const
            ).map(([key, label]) => (
              <label key={key}>
                <span>{label}</span>
                {key === "status" ? (
                  <select
                    className="ops-input"
                    onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                    value={editing.status}
                  >
                    <option value="draft">draft</option>
                    <option value="publish">publish</option>
                    <option value="archived">archived</option>
                  </select>
                ) : (
                  <input
                    className="ops-input"
                    onChange={(e) => setEditing({ ...editing, [key]: e.target.value })}
                    required={key === "slug" || key === "name_en"}
                    value={editing[key]}
                  />
                )}
              </label>
            ))}
          </div>
          <label className="ops-form__full">
            <span>Blurb EN</span>
            <textarea
              className="ops-input ops-textarea"
              onChange={(e) => setEditing({ ...editing, blurb_en: e.target.value })}
              rows={3}
              value={editing.blurb_en}
            />
          </label>
          <label className="ops-form__full">
            <span>Blurb ZH</span>
            <textarea
              className="ops-input ops-textarea"
              onChange={(e) => setEditing({ ...editing, blurb_zh: e.target.value })}
              rows={3}
              value={editing.blurb_zh}
            />
          </label>
          <div className="ops-toolbar">
            <button className="ops-btn ops-btn--primary" disabled={save.isPending} type="submit">
              Save
            </button>
            <button className="ops-btn ops-btn--ghost" onClick={() => setEditing(null)} type="button">
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {q.isLoading ? <p className="ops-muted">Loading…</p> : null}
      {q.data ? (
        <div className="ops-table-wrap ops-panel">
          <table className="ops-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>Heat</th>
                <th>Card</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((d) => (
                <tr
                  className={picked === d.id ? "is-selected" : undefined}
                  key={d.id}
                  onClick={() => setPicked(d.id)}
                >
                  <td>
                    <div className="ops-row-main">
                      <Thumb src={d.image_src} />
                      <div className="ops-row-copy">
                        <strong>{d.name_en}</strong>
                        <span className="ops-tags">
                          <Pill value={d.heat} />
                          {d.name_zh ? <span className="ops-muted">{d.name_zh}</span> : null}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <code>{d.slug}</code>
                  </td>
                  <td>{d.heat}</td>
                  <td>{d.image_src && d.blurb_en ? <span className="ops-ready">Complete</span> : <span className="ops-gap">Missing {[!d.image_src && "image", !d.blurb_en && "blurb"].filter(Boolean).join(", ")}</span>}</td>
                  <td><Pill value={d.status} /></td>
                  <td>
                    <button
                      className="ops-btn ops-btn--ghost"
                      onClick={() =>
                        setEditing({
                          slug: d.slug,
                          status: d.status,
                          heat: d.heat,
                          name_en: d.name_en,
                          name_zh: d.name_zh,
                          blurb_en: d.blurb_en,
                          blurb_zh: d.blurb_zh,
                          image_src: d.image_src,
                        })
                      }
                      type="button"
                    >
                      Edit
                    </button>
                    {d.status !== "archived" ? (
                      <button
                        className="ops-btn ops-btn--ghost"
                        onClick={() => archive.mutate(d.slug)}
                        type="button"
                      >
                        Archive
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      </div>
      <CityPreview item={preview} />
    </SplitPane>
  );
}
